/**
 * PORTED — settings chrome gap after RO-10-09 deleted excellent/app.
 * Origin: `excellent/app/settings/_rail.tsx`.
 *
 * Changes: next/link + next/navigation → @excellent/ui/router. Pushes a
 * side:"right" nested tree into ContextRail so /settings lights the reserved
 * right margin. Mounted from RootShell when the path is under /settings (the
 * Next host mounted it from app/settings/layout.tsx).
 *
 * Nav model is pure data from `@excellent/core/settings-tree` (no server
 * barrel — vite-guard forbids `@excellent/core/server` in the client graph).
 */

import { useMemo, type ComponentType, type SVGProps } from "react";
import {
  cn,
  useProvideRailExtension,
  type RailExtension,
  type RailExtensionTreeGroup,
  type SettingsNavGroup,
  type SettingsNavItem,
} from "@excellent/ui";
import {
  AppWindow,
  Bot,
  Building2,
  Contact,
  Package,
  Palette,
  Plug,
  User,
  Users,
  Wrench,
} from "@excellent/ui/icons";
import { RouterLink as Link, usePathname } from "@excellent/ui/router";
import { buildSettingsNav } from "@excellent/core/settings-tree";

/**
 * The settings navigation, floated into the shell's right-gutter `ContextRail`
 * — the SAME mechanism the docs reader, Database, and Agents ▸ Analytics use.
 * Mounted once from RootShell on `/settings/*`; renders nothing into the body
 * on desktop (the shell's `ContextRail` draws the pushed extension in the right
 * gutter, hugging the content well, so the page body stays full-width +
 * left-aligned).
 *
 * The rail is a NESTED TREE (`RailExtension.tree`): every settings group is a
 * row, and the ACTIVE group's pages reveal BENEATH it in the same animated
 * accordion + sliding marker the left ModeRail uses for a mode's sub-views. A
 * consolidated group's pages are scroll-spied in-page anchors; a multi-page
 * group's are leaf routes (active = the matched route). The shell renders the
 * tree, runs the accordion, and slides the whole rail in toward the page.
 *
 * Below `lg` the rail hides; this component renders an in-body horizontal nav
 * strip as the fallback (the old left `<aside>` was `md:`-only too, so this is
 * strictly more coverage). See docs/design/SETTINGS-REDESIGN.md.
 */

type IconType = ComponentType<SVGProps<SVGSVGElement>>;
const ICONS: Record<string, IconType> = {
  User,
  Building2,
  Palette,
  Package,
  Plug,
  Bot,
  Wrench,
  Users,
  Contact,
  AppWindow,
};
function iconFor(key: string): IconType {
  return ICONS[key] ?? Package;
}

const pageOf = (href: string): string => href.split("#")[0]!;
const targetMatches = (pathname: string, target: string): boolean =>
  pathname === target || pathname.startsWith(target + "/");

/** Longest matching target (landing page or any leaf), 0 when nothing matches —
 *  the most specific group wins (Team nests under Workspace, etc.). Mirrors the
 *  retired left SettingsNav so active-group selection is identical. */
function groupMatchLength(pathname: string, group: SettingsNavGroup): number {
  let best = targetMatches(pathname, group.href) ? group.href.length : 0;
  for (const it of group.items) {
    const target = it.activeHref ?? pageOf(it.href);
    if (targetMatches(pathname, target) && target.length > best) best = target.length;
  }
  return best;
}

function pickActiveGroup(
  groups: SettingsNavGroup[],
  pathname: string,
): SettingsNavGroup | null {
  let best: SettingsNavGroup | null = null;
  let bestLen = 0;
  for (const g of groups) {
    const len = groupMatchLength(pathname, g);
    if (len > bestLen) {
      best = g;
      bestLen = len;
    }
  }
  return best ?? groups[0] ?? null;
}

/** The most-specific leaf ITEM match within a group (so an "Overview" root
 *  doesn't stay lit on every deeper sibling). Returns the item so we can read
 *  its navigation href for the marker. */
function routeActiveItem(
  group: SettingsNavGroup,
  pathname: string,
): SettingsNavItem | null {
  let best: SettingsNavItem | null = null;
  let bestLen = 0;
  for (const it of group.items) {
    const target = it.activeHref ?? pageOf(it.href);
    if (targetMatches(pathname, target) && target.length > bestLen) {
      best = it;
      bestLen = target.length;
    }
  }
  return best;
}

/** Stable across renders — pure data, no args. */
const SETTINGS_NAV: SettingsNavGroup[] = buildSettingsNav();

export function SettingsRail() {
  const pathname = usePathname();
  const groups = SETTINGS_NAV;
  const active = pickActiveGroup(groups, pathname);

  // Build the nested tree: every group + (for the active group) its pages, which
  // the shell's ContextRail reveals beneath the active group in an accordion. A
  // consolidated group's items are in-page anchors (scroll-spied by the shell); a
  // multi-page group's are leaf routes, with `activeItemHref` lighting the marker.
  const extension = useMemo<RailExtension | null>(() => {
    if (!active) return null;
    const tree: RailExtensionTreeGroup[] = groups.map((g) => {
      const isActive = g.key === active.key;
      const Icon = iconFor(g.iconKey);
      const activeItem =
        isActive && !g.consolidated ? routeActiveItem(g, pathname) : null;
      return {
        key: g.key,
        label: g.label,
        href: g.href,
        icon: <Icon strokeWidth={1.5} className="size-3.5" />,
        active: isActive,
        consolidated: g.consolidated,
        items: g.items.map((it) => ({
          href: g.consolidated ? it.href : pageOf(it.href),
          label: it.label,
          anchor: g.consolidated ? it.anchor : undefined,
        })),
        activeItemHref: activeItem ? pageOf(activeItem.href) : undefined,
      };
    });

    return {
      side: "right",
      placement: "reserve",
      // Hug the settings content column (SettingsPageShell = max-w-3xl = 48rem),
      // so the rail sits just right of the page rather than the 1200px well edge.
      reserveContentRem: 48,
      groupLabel: "Settings",
      activeHref: active.href,
      links: [],
      toc: [],
      tree,
    };
  }, [groups, pathname, active]);

  useProvideRailExtension(extension);

  // < lg fallback: a horizontal strip of groups + the active group's pages.
  // The original only rendered active-group pages, which left mobile users
  // trapped inside one group with no way to reach Workspace / Agents / etc.
  // Groups row first, then the active group's leaf items.
  return (
    <div className="lg:hidden">
      <MobileGroupNav groups={groups} activeKey={active?.key ?? null} />
      {active ? <MobileItemNav group={active} pathname={pathname} /> : null}
    </div>
  );
}

/** Top-level settings groups — always visible < lg so every group is one tap away. */
function MobileGroupNav({
  groups,
  activeKey,
}: {
  groups: SettingsNavGroup[];
  activeKey: string | null;
}) {
  return (
    <nav
      aria-label="Settings groups"
      className="flex gap-1 overflow-x-auto border-b border-border/60 px-6 py-2 no-scrollbar"
    >
      {groups.map((g) => {
        const isActive = g.key === activeKey;
        return (
          <Link
            key={g.key}
            href={g.href}
            className={cn(
              "inline-flex h-7 items-center whitespace-nowrap rounded-full px-3 type-body transition-colors",
              isActive
                ? "bg-muted font-medium text-foreground"
                : "text-muted-foreground hover:bg-muted/50 hover:text-foreground",
            )}
          >
            {g.label}
          </Link>
        );
      })}
    </nav>
  );
}

/** The active group's leaf pages (or anchors on the consolidated Appearance page). */
function MobileItemNav({
  group,
  pathname,
}: {
  group: SettingsNavGroup;
  pathname: string;
}) {
  if (group.items.length === 0) return null;
  // Single-item groups (Personal, Apps) don't need a second strip.
  if (group.items.length === 1 && !group.consolidated) return null;

  const activeItem = routeActiveItem(group, pathname);
  const activeTarget = activeItem
    ? (activeItem.activeHref ?? pageOf(activeItem.href))
    : null;
  return (
    <nav
      aria-label={`${group.label} settings`}
      className="flex gap-4 overflow-x-auto border-b border-border/60 px-6 pt-2 no-scrollbar"
    >
      {group.items.map((it: SettingsNavItem) => {
        const target = it.activeHref ?? pageOf(it.href);
        const isActive =
          group.consolidated && pathname === group.href
            ? false
            : activeTarget === target;
        return (
          <Link
            key={it.href}
            href={it.href}
            className={cn(
              "whitespace-nowrap border-b-2 pb-2 type-body transition-colors",
              isActive
                ? "border-foreground font-medium text-foreground"
                : "border-transparent text-muted-foreground hover:text-foreground",
            )}
          >
            {it.label}
          </Link>
        );
      })}
    </nav>
  );
}
